import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils/helpers'
import { SectionInfoIcon } from '@/components/ui/SectionInfoIcon'
import styles from './SectionCard.module.css'

export function SectionCard({
  icon: Icon,
  number,
  title,
  description,
  children,
  className,
  testId,
  id,
  infoContent,
  infoTestId,
  headerActions,
  titleTestId,
}: {
  icon?: LucideIcon
  /**
   * Angular config/company section badge — gray circle with section index
   * (`h2 span` + CSS counter). Prefer this over `icon` for Angular parity.
   */
  number?: number
  title?: string
  description?: string
  children: ReactNode
  className?: string
  testId?: string
  id?: string
  /** Optional green (i) help tooltip beside the section title */
  infoContent?: ReactNode
  infoTestId?: string
  /** Optional controls aligned to the right of the section heading row */
  headerActions?: ReactNode
  /** Optional `data-testid` on the section title heading */
  titleTestId?: string
}) {
  const hasHeader = Boolean(Icon || number != null || title || headerActions)
  const showNumber = number != null && Number.isFinite(number)

  return (
    <section id={id} className={cn(styles.section, className)} data-testid={testId}>
      {hasHeader && (
        <div className={styles.sectionHeader}>
          <div className="flex flex-col gap-1 min-w-0">
            {title && (
              <h2 className={styles.sectionTitle} data-testid={titleTestId}>
                {showNumber ? (
                  <span className={styles.sectionNumber} aria-hidden="true">
                    {number}
                  </span>
                ) : Icon ? (
                  <span className={styles.sectionTitleIcon} aria-hidden="true">
                    <Icon className="h-4 w-4" />
                  </span>
                ) : null}
                <span className={styles.sectionTitleLabel}>
                  <span>{title}</span>
                  {infoContent != null ? (
                    <SectionInfoIcon content={infoContent} data-testid={infoTestId} />
                  ) : null}
                </span>
              </h2>
            )}
            {description && (
              <p
                className={cn(
                  'text-sm text-slate-500 leading-relaxed',
                  showNumber || Icon ? 'pl-10' : undefined,
                )}
              >
                {description}
              </p>
            )}
          </div>
          {headerActions != null ? (
            <div className={styles.sectionTitleActions}>{headerActions}</div>
          ) : null}
        </div>
      )}
      <div className={styles.sectionBody}>{children}</div>
    </section>
  )
}
